import React, { useEffect, useState } from 'react'
import { OtpInput } from './OtpInput'
import { useAuthStore } from '../../stores/authStore'

interface MfaLoginPromptProps {
  email: string
  tempSessionId: string
  onSuccess: () => void
  onCancel: () => void
}

export const MfaLoginPrompt: React.FC<MfaLoginPromptProps> = ({
  email,
  tempSessionId,
  onSuccess,
  onCancel
}) => {
  const verifyMfa = useAuthStore((state) => state.verifyMfa)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [codeSent, setCodeSent] = useState(false)

  const handleRequestOtp = async (): Promise<void> => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`${process.env.VITE_API_URL}/api/auth/mfa/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, tempSessionId })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send verification code')
      }

      setCodeSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (otpCode: string): Promise<void> => {
    setLoading(true)
    setError('')

    try {
      await verifyMfa(email, otpCode, false)
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void handleRequestOtp()
  }, [email, tempSessionId])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white/10 backdrop-blur-md rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blueSparkle/20 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-blueSparkle"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-whiteout mb-2">Two-Factor Authentication</h3>
          <p className="text-gray-400">Enter the verification code sent to your email</p>
          {codeSent && <p className="text-sm text-gray-500 mt-2">Code sent to {email}</p>}
        </div>

        <div className="space-y-6">
          <OtpInput length={6} onComplete={handleVerifyOtp} error={error} disabled={loading} />

          <div className="text-center">
            <p className="text-sm text-gray-400 mb-2">Didn&apos;t receive the code?</p>
            <button
              onClick={handleRequestOtp}
              disabled={loading}
              className="text-sm text-blueSparkle hover:text-blue-400 font-medium disabled:text-blue-600"
            >
              Resend Code
            </button>
          </div>
        </div>

        <button
          onClick={onCancel}
          className="w-full mt-4 px-4 py-3 border border-gray-600 rounded-lg text-gray-300 hover:bg-white/5 transition-colors font-medium"
          disabled={loading}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
